
// --- Enums & Helpers ---

export enum TimeOfDay {
    DAY = 'DAY',
    NIGHT = 'NIGHT',
    DAWN = 'DAWN',
    DUSK = 'DUSK',
    MORNING = 'MORNING',
    EVENING = 'EVENING'
}

export enum Setting {
    INT = 'INT',
    EXT = 'EXT',
    INT_EXT = 'INT_EXT'
}

export enum SceneStatus {
    SCRIPTYARD = 'SCRIPTYARD',
    BONEYARD = 'BONEYARD'
}

export enum SceneType {
    NORMAL = 'NORMAL',
    FLASHBACK = 'FLASHBACK',
    MONTAGE = 'MONTAGE',
    DREAM = 'DREAM',
    FANTASY = 'FANTASY',
    INTERCUT = 'INTERCUT',
}

export interface Sequence {
    id: string;
    name: string;
    color: string;
    sceneIds: string[];
}

export const decimalToEighths = (decimal: number): string => {
    if (!Number.isFinite(decimal) || decimal <= 0) return '0';

    const totalEighths = Math.round(decimal * 8);
    const whole = Math.floor(totalEighths / 8);
    const eighths = totalEighths % 8;

    if (eighths === 0) return `${whole}`;
    return whole > 0 ? `${whole} ${eighths}/8` : `${eighths}/8`;
};

export const eighthsToDecimal = (str: string): number => {
    if (!str) return 0;

    const normalized = String(str).trim();
    if (!normalized) return 0;

    const numeric = Number(normalized);
    if (Number.isFinite(numeric)) return numeric;

    const parts = normalized.split(/\s+/);
    let whole = 0;
    let fraction = 0;

    const parseFraction = (value: string): number => {
        const [rawNum, rawDen] = value.split('/');
        const num = Number(rawNum);
        const den = Number(rawDen);
        if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return 0;
        return num / den;
    };

    if (parts.length >= 2 && parts[1].includes('/')) {
        whole = Number(parts[0]) || 0;
        fraction = parseFraction(parts[1]);
    } else if (parts[0].includes('/')) {
        fraction = parseFraction(parts[0]);
    } else {
        whole = Number(parts[0]) || 0;
    }

    return whole + fraction;
};

// --- Types ---

export interface BreakdownTagMetadata {
    notes?: string;
    tagId?: string;
    nonSpeaking?: boolean;
    hidden?: boolean;
}

export interface BreakdownCategory {
    id: string;
    name: string; // e.g., "CAST", "PROPS"
    count: number;
    items: string[];
    color?: string;
    /** Metadata keyed by canonical item identity, never by array position. */
    itemMetadata?: Record<string, BreakdownTagMetadata>;
}

export interface ScriptLine {
    id: string;
    type: 'scene-heading' | 'action' | 'character' | 'dialogue' | 'parenthetical' | 'transition';
    content: string;
}

export interface Location {
    id: string;
    name: string;
    address?: string;
    notes?: string;
    /** Optimistic-concurrency token for protected location mutations. */
    revision?: number;
    /** User that produced the current location revision, when known. */
    updatedBy?: string | null;
}

export interface SceneData {
    /** Manual production progress, independent from Script/Boneyard placement. */
    shootingStatus?: import('../models/sceneShooting').ShootingStatus;
    shootingRevision?: number;
    shootingSourceRevision?: number | null;
    id: string;
    number: string;
    slug: string;
    thumbnailUrl?: string;
    shotCount?: number;
    categories: BreakdownCategory[];
    scriptLines?: ScriptLine[];
    synopsis?: string;
    pages?: string;     // Display string (e.g., "1 4/8")
    pageCount?: number; // Numeric value for calculations
    color?: string;
    unit?: string;
    unitId?: string;
    locationId?: string;
    estimatedTime?: string;
    estimatedHours?: number;
    timeOfDay?: TimeOfDay;
    setting?: Setting;
    status?: SceneStatus;
    sceneType?: SceneType;
    sequenceId?: string;
    notes?: string;
    sourceHeading?: string;
    sourcePageStart?: number;
    sourcePageEnd?: number;
    pageCountMethod?: 'source' | 'estimated';
    qualityWarnings?: string[];
    /** Optimistic-concurrency token for scheduling-only fields. */
    planningRevision?: number;
    /** User that produced the current planning revision, when known. */
    planningUpdatedBy?: string | null;
    /** Optimistic-concurrency token for script and breakdown content. */
    contentRevision?: number;
    /** User that produced the current content revision, when known. */
    contentUpdatedBy?: string | null;
}

export const getScenePageCount = (scene: Pick<SceneData, 'pages' | 'pageCount'>): number => {
    if (scene.pageCount !== undefined
        && Number.isFinite(scene.pageCount)
        && scene.pageCount >= 0) {
        return scene.pageCount;
    }
    const parsedPages = scene.pages ? eighthsToDecimal(scene.pages) : 0;
    return Number.isFinite(parsedPages) && parsedPages >= 0 ? parsedPages : 0;
};

export const getScenePageDisplay = (scene: Pick<SceneData, 'pages' | 'pageCount'>): string => {
    if (scene.pageCount !== undefined
        && Number.isFinite(scene.pageCount)
        && scene.pageCount >= 0) {
        return decimalToEighths(scene.pageCount);
    }
    const trimmedPages = scene.pages?.trim();
    if (trimmedPages) return trimmedPages;
    return decimalToEighths(getScenePageCount(scene));
};

/**
 * Legacy AI breakdown output. This is a scene-level production suggestion,
 * not the operational shooting-day CallSheetDocument used by the editor and
 * PDF exporter.
 */
export interface BreakdownCallSheetSuggestion {
    date: string;
    call_time: string;
    location: string;
    scenes: string[];
    cast: string[];
    props: string[];
    equipment: string[];
    notes: string[];
}

export interface BreakdownMeta {
    scene_count: number;
    ai_failed_scene_count?: number;
    ai_failed_scene_numbers?: string[];
}

export interface BreakdownResult {
    scenes: SceneData[];
    locations: Location[];
    callSheets: BreakdownCallSheetSuggestion[];
    meta: BreakdownMeta;
    /** SHA-256 of the exact canonical API payload retained in pending_breakdown. */
    pendingFingerprint?: string;
    /** Exact API payload used for a database-side JSONB stale-payload comparison. */
    pendingPayload?: unknown;
}

export interface BreakdownProgress {
    status: 'starting' | 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';
    stage?: 'queued' | 'extracting' | 'analyzing' | 'provider_wait';
    reconnecting?: boolean;
    totalScenes: number;
    completedScenes: number;
    successfulScenes?: number;
    failedScenes?: number;
    pendingScenes?: number;
    progressUpdatedAt?: string;
    recoveryAfter?: string;
    percent: number;
    error?: string;
}
